import React, { useEffect, useState } from 'react';

import { useAuthStore } from '../../store/auth';
import { useCompanyStore } from '../../store/company';
import { authApi } from '../../api/auth';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import './AuthGuard.css';

const AuthGuard = ({ children }) => {
  const { token, setUser, isAuthenticated } = useAuthStore();
  const { setCompanies, setCompanyId, setCompany } = useCompanyStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Resiliência: tentar novamente automaticamente em erros transitórios (ex.: aborted/network)
        const fetchMeWithRetry = async (retries = 2) => {
          let lastError = null;
          for (let attempt = 0; attempt <= retries; attempt++) {
            try {
              return await authApi.me();
            } catch (err) {
              lastError = err;
              const msg = (err?.message || '').toLowerCase();
              const isAbort =
                err?.code === 'ERR_CANCELED' ||
                msg.includes('aborted') ||
                msg.includes('cancel') ||
                msg.includes('binding');
              const isNetwork = !err?.response;
              // Repetir apenas para aborts/timeouts/erros de rede
              if (isAbort || isNetwork) {
                await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
                continue;
              }
              throw err;
            }
          }
          throw lastError;
        };

        const response = await fetchMeWithRetry(2);

        // A resposta tem a estrutura { user: { ... } }
        const userData = response.data.user || response.data;

        // Atualizar dados do usuário
        setUser(userData);

        // Atualizar empresas do usuário
        const companies = userData.companies || [];
        setCompanies(companies);

        // Selecionar automaticamente a primeira empresa se não houver empresa selecionada
        const currentCompanyId = useCompanyStore.getState().companyId;

        if (!currentCompanyId && companies.length > 0) {
          const firstCompany = companies[0];
          console.log('AuthGuard: Setting first company', {
            companyId: firstCompany.id,
            companyName: firstCompany.name,
            company: firstCompany,
          });
          setCompanyId(firstCompany.id);
          setCompany(firstCompany);
        } else if (currentCompanyId) {
          // Verificar se a empresa atual ainda existe
          const currentCompany = companies.find(c => c.id === currentCompanyId);
          if (currentCompany) {
            setCompany(currentCompany);
          } else if (companies.length > 0) {
            // Se a empresa atual não existe mais, usar a primeira disponível
            const firstCompany = companies[0];
            console.log('AuthGuard: Current company not found, switching to first company', {
              oldCompanyId: currentCompanyId,
              newCompanyId: firstCompany.id,
              companyName: firstCompany.name,
            });
            setCompanyId(firstCompany.id);
            setCompany(firstCompany);
          }
        }

        setError(null);
      } catch (err) {
        console.error('Erro ao verificar autenticação:', err);
        setError('Erro ao carregar dados do usuário');

        // Token pode estar expirado, limpar dados e redirecionar para login
        if (err.response?.status === 401) {
          useAuthStore.getState().clearToken();
          useCompanyStore.getState().clearCompany();
          window.location.href = '/login';
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [token, setUser, setCompanies, setCompanyId, setCompany]);

  if (loading) {
    return <LoadingSpinner size="large" text="Verificando autenticação..." fullScreen />;
  }

  if (error) {
    return (
      <ErrorState
        title="Erro de Autenticação"
        description="Não foi possível verificar sua autenticação. Verifique sua conexão e tente novamente."
        error={error}
        onRetry={() => window.location.reload()}
        onGoBack={() => (window.location.href = '/login')}
        size="large"
      />
    );
  }

  if (!isAuthenticated()) {
    return (
      <ErrorState
        title="Acesso Não Autorizado"
        description="Você precisa fazer login para acessar esta página."
        onGoBack={() => (window.location.href = '/login')}
        size="large"
      />
    );
  }

  return children;
};

export default AuthGuard;
